// ============================================================================
// ASSIGNEEPICKER - Selector de "Persona asignada" (estilo Jira)
// ============================================================================
// Muestra el avatar de la persona asignada, o un icono genérico "Sin asignar"
// si no hay nadie. Al pulsar abre un popover (posición fija, para no quedar
// recortado por el overflow de la tabla) con buscador + lista de usuarios del
// tenant + opción "Sin asignar". Devuelve el id elegido (o null) por onChange.
// ============================================================================

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { User, Search, Check, X } from 'lucide-react';
import Avatar from './Avatar';
import { useAssignableUsers } from '../services/assignableUsers';
import { useIsPhone } from './shared/useMediaQuery';
import '../styles/assignee-picker.css';

const MENU_WIDTH = 230;
const MENU_MAX_HEIGHT = 292; // buscador + lista (max 240px) + bordes

const AssigneePicker = ({
  value,
  assigneeName,
  assigneeAvatar,
  onChange,
  size = 26,
  disabled = false,
  align = 'right',
  showName = false,
  className = '',
}) => {
  const users = useAssignableUsers();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  // En teléfono la lista (más de cinco opciones) se abre como hoja inferior
  const isPhone = useIsPhone();
  const asSheet = isPhone && users.length + 1 > 5;

  const selected = useMemo(
    () => (value ? users.find((u) => Number(u.id) === Number(value)) : null),
    [users, value]
  );
  // Prioriza la lista en vivo (así tras reasignar se actualiza al instante);
  // cae al dato que trae la tarea como respaldo inicial (antes de cargar la lista).
  const displayName = selected?.nombre || assigneeName || '';
  const displayAvatar = selected?.avatar || assigneeAvatar || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.nombre.toLowerCase().includes(q));
  }, [users, query]);

  const computePos = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    let left = align === 'right' ? rect.right - MENU_WIDTH : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8));
    // Si no cabe por debajo del trigger, se desliza hacia arriba lo justo
    // para que el menú entre entero en el viewport.
    let top = rect.bottom + 6;
    if (top + MENU_MAX_HEIGHT > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - 8 - MENU_MAX_HEIGHT);
    }
    setMenuPos({ top, left });
  };

  const toggle = () => {
    if (disabled) return;
    if (!open) {
      computePos();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // Cerrar al hacer clic fuera, con Escape, o al hacer scroll/resize.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollResize = (e) => {
      // El scroll interno de la lista del popover no debe cerrarlo.
      if (e?.target instanceof Node && wrapRef.current?.contains(e.target)) return;
      // La hoja inferior no depende de la posición del trigger: ni el scroll
      // de la página ni el resize del teclado virtual la cierran.
      if (asSheet) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open, asSheet]);

  // Hoja inferior: foco inicial en el diálogo (sin abrir el teclado)
  useEffect(() => {
    if (open && asSheet) menuRef.current?.focus();
  }, [open, asSheet]);

  const pick = (id) => {
    setOpen(false);
    setQuery('');
    const same = Number(id) === Number(value) || (id == null && value == null);
    if (!same) onChange?.(id);
  };

  const dim = `${size}px`;

  return (
    <div className={`ap-wrap ${className}`} ref={wrapRef} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        className={`ap-trigger ${showName ? 'ap-trigger-labeled' : ''} ${open ? 'ap-open' : ''}`}
        onClick={toggle}
        disabled={disabled}
        title={value ? displayName : 'Sin asignar'}
        aria-label={value ? `Asignada a ${displayName}` : 'Sin asignar'}
      >
        {value ? (
          <Avatar name={displayName} src={displayAvatar} size={size} />
        ) : (
          <span className="ap-unassigned" style={{ width: dim, height: dim }}>
            <User size={Math.round(size * 0.55)} />
          </span>
        )}
        {showName && (
          <span className="ap-trigger-label">{value ? displayName : 'Sin asignar'}</span>
        )}
      </button>

      {open && asSheet && (
        <div className="ap-sheet-overlay" aria-hidden="true" onClick={() => setOpen(false)} />
      )}
      {open && (
        <div
          ref={menuRef}
          className={`ap-menu${asSheet ? ' ap-sheet u-sheet' : ''}`}
          style={{ top: menuPos.top, left: menuPos.left }}
          role={asSheet ? 'dialog' : undefined}
          aria-modal={asSheet ? 'true' : undefined}
          aria-label={asSheet ? 'Persona asignada' : undefined}
          tabIndex={asSheet ? -1 : undefined}
        >
          {asSheet && (
            <div className="ap-sheet-header">
              <span className="ap-sheet-title">Persona asignada</span>
              <button type="button" className="ap-sheet-close" onClick={() => setOpen(false)} aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>
          )}
          <div className="ap-search">
            <Search size={14} />
            <input
              type="text"
              autoFocus={!asSheet}
              placeholder="Buscar persona…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="ap-list">
            <button type="button" className="ap-option" onClick={() => pick(null)}>
              <span className="ap-unassigned ap-unassigned-sm">
                <User size={13} />
              </span>
              <span className="ap-option-name">Sin asignar</span>
              {!value && <Check size={14} className="ap-check" />}
            </button>

            {filtered.map((u) => (
              <button key={u.id} type="button" className="ap-option" onClick={() => pick(u.id)}>
                <Avatar name={u.nombre} src={u.avatar} size={24} />
                <span className="ap-option-name">{u.nombre}</span>
                {Number(value) === Number(u.id) && <Check size={14} className="ap-check" />}
              </button>
            ))}

            {filtered.length === 0 && <div className="ap-empty">Sin resultados</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssigneePicker;
